import type {
  IField,
  IOrderComponentDescriptor,
} from './interfaces/MetadataInterfaces';
import { isOrderComponentSupported } from './orderComponentCapabilities';

export type VisibilityScalar = string | number | boolean | null;

export interface VisibilityCondition {
  field: string;
  operator: 'equals' | 'in';
  values: VisibilityScalar[];
}

export interface OrderComponentVisibilityInput {
  fields: IField[];
  answers: Record<string, unknown>;
  availableTimeServiceCodes: string[];
  catalog?: IOrderComponentDescriptor[];
}

export interface OrderComponentVisibilityState {
  visible: boolean;
  error?: string;
}

export const MAX_VISIBILITY_ALLOWLIST_SIZE = 32;

const VISIBLE: OrderComponentVisibilityState = { visible: true };
const HIDDEN: OrderComponentVisibilityState = { visible: false };
const failed = (error: string): OrderComponentVisibilityState => ({
  visible: false,
  error,
});

const isScalar = (value: unknown): value is VisibilityScalar =>
  value === null ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

const sameName = (left: string, right: string) =>
  left.toLowerCase() === right.toLowerCase();

export const answerFor = (
  answers: Record<string, unknown>,
  name: string
): unknown => {
  const key = Object.keys(answers).find((candidate) =>
    sameName(candidate, name)
  );
  return key == null ? undefined : answers[key];
};

const parseCondition = (
  name: string,
  expected: unknown,
  label: string
): VisibilityCondition => {
  if (isScalar(expected)) {
    return { field: name, operator: 'equals', values: [expected] };
  }
  if (expected && typeof expected === 'object' && !Array.isArray(expected)) {
    const list = (expected as { in?: unknown }).in;
    if (
      Object.keys(expected).length === 1 &&
      Array.isArray(list) &&
      list.length >= 1 &&
      list.length <= MAX_VISIBILITY_ALLOWLIST_SIZE &&
      list.every(isScalar)
    ) {
      return { field: name, operator: 'in', values: list };
    }
    throw new Error(
      `Visibility rules for "${label}" use an unsupported condition.`
    );
  }
  throw new Error(`Visibility rules for "${label}" must use scalar values.`);
};

export const parseVisibilityConditions = (
  dependees: string,
  label: string
): VisibilityCondition[] => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(dependees);
  } catch {
    throw new Error(`Visibility rules for "${label}" are invalid.`);
  }
  if (
    !parsed ||
    typeof parsed !== 'object' ||
    Array.isArray(parsed) ||
    Object.keys(parsed).length === 0
  ) {
    throw new Error(`Visibility rules for "${label}" are unsupported.`);
  }
  const conditions = Object.entries(parsed).map(([name, expected]) =>
    parseCondition(name, expected, label)
  );
  const names = conditions.map((condition) => condition.field.toLowerCase());
  if (new Set(names).size !== names.length) {
    throw new Error(
      `Visibility rules for "${label}" refer to the same field more than once.`
    );
  }
  return conditions;
};

export const serializeVisibilityConditions = (
  conditions: VisibilityCondition[]
): string =>
  JSON.stringify(
    Object.fromEntries(
      conditions.map((condition) => [
        condition.field,
        condition.operator === 'in'
          ? { in: condition.values }
          : condition.values[0],
      ])
    )
  );

const conditionMatches = (condition: VisibilityCondition, answer: unknown) =>
  condition.values.some((value) => answer === value);

const usesVisibilityRule = (field: IField) =>
  field.fieldType === 'OrderComponent' ||
  Boolean(field.dependees?.trim().startsWith('{'));

export const evaluateOrderComponentVisibility = ({
  fields,
  answers,
  availableTimeServiceCodes,
  catalog,
}: OrderComponentVisibilityInput): Map<
  string,
  OrderComponentVisibilityState
> => {
  const fieldsByName = new Map(
    fields.map((field) => [field.name.toLowerCase(), field])
  );
  const availableCodes = new Set(
    availableTimeServiceCodes.map((code) => code.toLowerCase())
  );
  const speedFields = fields.filter(
    (field) =>
      field.fieldType === 'OrderComponent' &&
      field.componentCode === 'TIME_SERVICE'
  );
  const states = new Map<string, OrderComponentVisibilityState>();

  const answerOf = (field: IField): unknown => {
    const answer = answerFor(answers, field.name);
    if (field.componentCode !== 'TIME_SERVICE') return answer;
    return typeof answer === 'string' &&
      availableCodes.has(answer.toLowerCase())
      ? answer
      : undefined;
  };

  const stateOf = (
    field: IField,
    visiting: ReadonlySet<string>
  ): OrderComponentVisibilityState => {
    const key = field.name.toLowerCase();
    const known = states.get(key);
    if (known) return known;
    if (visiting.has(key)) {
      return failed(`Visibility rules contain a cycle at "${field.name}".`);
    }
    const state = computeState(field, new Set(visiting).add(key));
    states.set(key, state);
    return state;
  };

  const ruleState = (
    field: IField,
    visiting: ReadonlySet<string>
  ): OrderComponentVisibilityState => {
    if (!usesVisibilityRule(field) || !field.dependees) {
      if (field.effect !== 'Hide') return VISIBLE;
      return field.fieldType === 'OrderComponent'
        ? failed(`Hidden field "${field.label}" has no visibility rule.`)
        : HIDDEN;
    }
    let conditions: VisibilityCondition[];
    try {
      conditions = parseVisibilityConditions(field.dependees, field.label);
    } catch (error) {
      return failed(error instanceof Error ? error.message : 'Invalid rule.');
    }
    const outcomes = conditions.map((condition) => {
      const controller = fieldsByName.get(condition.field.toLowerCase());
      if (!controller) {
        return failed(
          `Visibility rules for "${field.label}" refer to a missing field.`
        );
      }
      const controllerState = stateOf(controller, visiting);
      if (controllerState.error) return controllerState;
      const answer = controllerState.visible ? answerOf(controller) : undefined;
      return conditionMatches(condition, answer) ? VISIBLE : HIDDEN;
    });
    const failure = outcomes.find((outcome) => outcome.error);
    if (failure) return failure;
    const matches = outcomes.every((outcome) => outcome.visible);
    if (field.effect === 'Show') return matches ? VISIBLE : HIDDEN;
    if (field.effect === 'Hide') return matches ? HIDDEN : VISIBLE;
    return failed(`Visibility effect for "${field.label}" is unsupported.`);
  };

  const eligibilityState = (
    field: IField,
    visiting: ReadonlySet<string>
  ): OrderComponentVisibilityState => {
    const allowed = catalog?.find(
      (descriptor) => descriptor.code === field.componentCode
    )?.allowedTimeServiceCodes;
    if (field.fieldType !== 'OrderComponent' || !allowed) return VISIBLE;
    const [speedField] = speedFields;
    if (speedFields.length !== 1 || !speedField) return HIDDEN;
    const speed = stateOf(speedField, visiting);
    if (!speed.visible) return speed;
    const code = answerOf(speedField);
    return typeof code === 'string' && allowed.includes(code)
      ? VISIBLE
      : HIDDEN;
  };

  const computeState = (
    field: IField,
    visiting: ReadonlySet<string>
  ): OrderComponentVisibilityState => {
    const descriptor = catalog?.find(
      (candidate) => candidate.code === field.componentCode
    );
    if (
      field.fieldType === 'OrderComponent' &&
      descriptor &&
      !isOrderComponentSupported(descriptor)
    ) {
      return failed(
        `Order Component "${field.label}" is not supported by this application.`
      );
    }
    const rule = ruleState(field, visiting);
    if (!rule.visible) return rule;
    const parent = field.parentName
      ? fieldsByName.get(field.parentName.toLowerCase())
      : undefined;
    if (parent) {
      const parentState = stateOf(parent, visiting);
      if (!parentState.visible) return parentState;
    }
    return eligibilityState(field, visiting);
  };

  fields.forEach((field) => stateOf(field, new Set()));
  return states;
};

export const hiddenOrderComponentNames = (
  input: OrderComponentVisibilityInput
): Set<string> => {
  const states = evaluateOrderComponentVisibility(input);
  return new Set(
    input.fields
      .filter(
        (field) =>
          field.fieldType === 'OrderComponent' &&
          !states.get(field.name.toLowerCase())?.visible
      )
      .map((field) => field.name)
  );
};

export const pruneHiddenOrderComponentAnswers = (
  input: OrderComponentVisibilityInput
): Record<string, unknown> => {
  const hidden = [...hiddenOrderComponentNames(input)];
  return Object.fromEntries(
    Object.entries(input.answers).filter(
      ([key]) => !hidden.some((name) => sameName(name, key))
    )
  );
};
